import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row, Card } from 'react-bootstrap';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { ActivityIndicator } from '@/components/ActivityIndicator/ActivityIndicator';
import { Srcset } from '@/components/Srcset/Srcset';
import { ContentPageLayout } from '@/components/Layouts/ContentPageLayout';
import { Pagination } from './components/BlogPagination';
import styles from './blog.module.scss';
import BlogFilters from './components/BlogFilters';
import blogService, { IBlogListItem, IBlogListRequest, IBlogListResponse } from './blogService';
import type * as Types from '@/pages/mijn-carel-lurvink/catalogus/stores/types';

type SectorName = 'Medical' | 'Food' | 'Facility' | 'Industry';

interface BlogProps {
  sectorAutoAdded: boolean;
  initialRequest: IBlogListRequest;
  initialResponse: IBlogListResponse | null;
  error: string | null;
}

const Blog = ({ initialRequest, initialResponse, error, sectorAutoAdded }: BlogProps) => {
  const { t } = useTranslation('blog');
  const router = useRouter();

  const [request, setRequest] = useState(initialRequest);
  const [response, setResponse] = useState(initialResponse);
  const { items = [], totalCount = 0, perPage = 0, page = 0, facets = [] } = response || {};

  const { sectors, categories } = request;
  const [showFilters, setShowFilters] = useState((!sectorAutoAdded && sectors.length > 0) || categories.length > 0);
  const [isLoading, setIsLoading] = useState(false);
  const isInitialRender = useRef(process.env.NODE_ENV === 'development' ? 2 : 1);
  const publicUrl = process.env.URL_WWW_EXTERNAL;

  const totalPages = Math.ceil(Number(totalCount) / perPage);

  const getSectorClassName = (sectorName: string) => {
    const sectorStyles = {
      Medical: styles.medical,
      Food: styles.food,
      Facility: styles.facility,
      Industry: styles.industry
    };
    return sectorStyles[sectorName as SectorName] || styles.defaultStyle;
  };

  const getSectorText = (sectorName: string) => {
    const sectorStyles = {
      Medical: styles.medicalText,
      Food: styles.foodText,
      Facility: styles.facilityText,
      Industry: styles.industryText
    };
    return sectorStyles[sectorName as SectorName] || styles.defaultStyle;
  };

  const getUrl = (path: string, newPage: number, request: IBlogListRequest): string => {
    const currentParams = new URLSearchParams();
    currentParams.set('page', newPage.toString());

    request.sectors.forEach((sector) => {
      currentParams.append('sector', sector);
    });

    request.categories.forEach((category) => {
      currentParams.append('category', category);
    });

    return `${path}?${currentParams.toString()}`;
  };

  useEffect(() => {
    if (isInitialRender.current > 0) {
      isInitialRender.current--;
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const data = await blogService.fetchBlogList(request);
        setResponse(data);

        const newUrl = getUrl(router.pathname, request.page, request);
        router.push(newUrl, undefined, { shallow: true });
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveSector = (sector: string) => {
    setRequest((prevRequest) => ({
      ...prevRequest,
      sectors: prevRequest.sectors.filter((sec) => sec !== sector),
      page: 1
    }));
  };

  const handleRemoveCategory = (category: string) => {
    setRequest((prevRequest) => ({
      ...prevRequest,
      categories: prevRequest.categories.filter((cat) => cat !== category)
    }));
  };

  return (
    <>
      <Head>
        <title>{`${t('blog-title')} - Carel Lurvink B.V.${page > 1 ? ` | Pagina ${page}` : ''}`}</title>
        {page > 1 ? (
          <link rel="canonical" href={`${publicUrl}${router.pathname}?page=${page}`} key="canonical" />
        ) : (
          <link rel="canonical" href={`${publicUrl}${router.pathname}`} key="canonical" />
        )}
        <meta name="description" content={t('meta.desc')} />
      </Head>
      {isLoading && <ActivityIndicator />}
      {error ? (
        <div>{error}</div>
      ) : (
        <>
          <Container>
            <div className="d-lg-flex justify-content-between mt-4 align-items-center">
              <h1>{t('blog-title')}</h1>
              <button
                onClick={() => setShowFilters((prevShow) => !prevShow)}
                className={`mb-2 text-white p-2 h3 mt-2 ${styles.sectorBackground}`}
              >
                <span className="fa fa-filter mr-1" aria-hidden="true"></span>
                {t('filters.button')}
              </button>
            </div>
            {(request.sectors.length > 0 || request.categories.length > 0) && (
              <div className={styles.FilterBadgeList}>
                <span className="text-nowrap mb-1 mr-2">{t('active.filters')}</span>

                {request?.sectors.map((sector) => (
                  <div className={styles.FilterBadge} key={sector}>
                    <button
                      className="badge btn bg-theme-2 text-white mr-2"
                      role="button"
                      rel={sector}
                      onClick={() => handleRemoveSector(sector)}
                    >
                      <span className={styles.value}>{sector}</span>
                      <span className="fa fa-times ml-2"></span>
                    </button>
                  </div>
                ))}

                {request?.categories.map((category) => (
                  <div className={styles.FilterBadge} key={category}>
                    <button
                      className="badge btn bg-theme-2 text-white mr-2"
                      role="button"
                      rel={category}
                      onClick={() => handleRemoveCategory(category)}
                    >
                      <span className={styles.value}>{category}</span>
                      <span className="fa fa-times ml-2"></span>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showFilters && items && <BlogFilters facets={facets} request={request} setRequest={setRequest} />}
            <Row className="mt-2">
              {items.length > 0 ? (
                <Col xs={12} lg={12} className={'mb-4 d-lg-flex'}>
                  <div className="w-100">
                    {' '}
                    <Link href={`${items[0].url}`}>
                      <Srcset image={items[0].image} className={styles.cardImage} />
                    </Link>
                  </div>
                  <div className={`w-100 ${styles.firstBlog} d-flex flex-column align-items-center justify-content-center`}>
                    <div>
                      {items[0].sectors?.length > 0 && items[0].sectors[0] && (
                        <div className="my-3 ml-2 ">
                          <span className={`${getSectorText(items[0].sectors[0])} font-weight-bold bg-white p-2`}>
                            {items[0].sectors[0]}
                          </span>
                        </div>
                      )}
                      <Link href={`${items[0].url}`}>
                        <h2 className="m-2 h2 text-lg">{items[0].title}</h2>
                      </Link>
                      {items[0].subtitle && <p className="ml-2 text-md-lg">{items[0].subtitle}</p>}
                    </div>
                  </div>
                </Col>
              ) : (
                <div className="d-flex justify-content-center w-100">
                  <h2 className="h1 my-7">{t('blogs.not-found')}</h2>
                </div>
              )}

              {items.slice(1).map((blog: IBlogListItem, idx: number) => (
                <Col xs={12} lg={6} className={'mb-4'} key={idx}>
                  <Card border="0" className="rounded-0 h-100">
                    <Link href={`${blog.url}`}>
                      <Srcset image={blog.image} className={styles.cardImage} />
                    </Link>
                    <div>
                      {blog.sectors?.length > 0 && blog.sectors[0] && (
                        <div className="my-3 ml-2 ">
                          <span className={`${getSectorClassName(blog.sectors[0])} font-weight-bold text-white p-2`}>
                            {blog.sectors[0]}
                          </span>
                        </div>
                      )}

                      <Link href={`${blog.url}`}>
                        <h3 className="m-2">{blog.title}</h3>
                      </Link>
                      {blog.subtitle && <p className="ml-2">{blog.subtitle}</p>}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
          <Pagination
            pageCount={totalPages}
            currentPage={page}
            getUrl={(newPage) => getUrl(router.pathname, newPage, request)}
            setRequest={setRequest}
          />
        </>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps<BlogProps> = async (context: GetServerSidePropsContext) => {
  const page = context.query.page && Number(context.query.page) > 1 ? Number(context.query.page) : 1;
  let sectorAutoAdded = false;
  let sectors: string[];

  if (!context.query.sector) {
    sectorAutoAdded = true;
    const cookieSector = context.req.cookies['cl_sector'] ?? 'Facility';
    const sector = cookieSector === 'Food' ? 'Pharmacy & Food' : cookieSector;
    sectors = [sector];
  } else {
    sectors = Array.isArray(context.query.sector) ? context.query.sector : context.query.sector ? [context.query.sector] : [];
  }

  const initialRequest: IBlogListRequest = {
    sectors,
    categories: Array.isArray(context.query.category) ? context.query.category : context.query.category ? [context.query.category] : [],
    page: page
  };

  try {
    const initialResponse = await blogService.fetchBlogList(initialRequest);

    return {
      props: {
        sectorAutoAdded,
        initialRequest,
        initialResponse,
        error: null
      }
    };
  } catch (error) {
    return {
      props: {
        sectorAutoAdded,
        initialRequest,
        initialResponse: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      }
    };
  }
};

Blog.getLayout = function getLayout(page: React.ReactNode, theme: AppTheme, categories: Types.Category[]) {
  return (
    <ContentPageLayout theme={theme} categories={categories}>
      {page}
    </ContentPageLayout>
  );
};

export default Blog;
